import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@src/environments/environment';
import { ImageMetadata, SignedUploadUrlResponse, LogoKind } from '../model/image-metadata.model';

@Injectable({
  providedIn: 'root'
})
export class ImageUploadService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get a signed URL for uploading an image to S3
   * @param filename The name of the file to upload
   * @param kind What the image belongs to (decides the key and who may write it)
   * @param organizationId Owning organization (not needed for 'person')
   * @returns Observable with the signed upload URL and the minted key
   */
  getSignedUploadUrl(filename: string, kind: LogoKind, organizationId?: string): Observable<SignedUploadUrlResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    const body = JSON.stringify({ Key: filename, kind, organizationId });
    return this.http.post<SignedUploadUrlResponse>(
      `${this.apiUrl}/logostore/getsignedurl`,
      body,
      httpOptions
    );
  }

  /**
   * Upload an image file to S3 using a signed URL
   * @param file The image file to upload
   * @param signedUrl The signed S3 upload URL
   * @returns Observable with the upload response
   */
  uploadImageToS3(file: File, signedUrl: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': file.type || 'application/octet-stream'
      }),
      withCredentials: true,
    };
    return this.http.put(signedUrl, file, httpOptions);
  }

  /**
   * Save image metadata to the database
   * @param metadata The image metadata to save
   * @param context The context (form, org, profile)
   * @param imgType The image type (e.g., 'form', 'logo', 'rect', 'square')
   * @returns Observable with the saved metadata
   */
  saveImageMetadata(metadata: ImageMetadata, context: string = 'form', imgType: string = 'form'): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    
    // Use the existing organization image endpoint that supports metadata storage
    // The endpoint is: POST /organization/{organizationId}/image/{imgtype}
    if (!metadata.organizationId) {
      throw new Error('organizationId is required to save image metadata');
    }
    
    // Prepare the body to match the backend expectation
    const body = {
      image: metadata.imageUrl,
      date_added: new Date().toISOString(),
      orgimageurl: metadata.imageUrl,
      ...metadata
    };
    
    const endpoint = `/organization/${metadata.organizationId}/image/${imgType}`;
    return this.http.post<any>(`${this.apiUrl}${endpoint}`, JSON.stringify(body), httpOptions);
  }

  /**
   * Get image metadata by ID
   * @param imageId The ID of the image
   * @returns Observable with the image metadata
   */
  getImageMetadata(imageId: string): Observable<ImageMetadata> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    return this.http.get<ImageMetadata>(`${this.apiUrl}/image/metadata/${imageId}`, httpOptions);
  }

  /**
   * Get image metadata for an organization
   * @param orgId The organization ID
   * @param type The image type (rect, square)
   * @returns Observable with the image metadata
   */
  getOrgImageMetadata(orgId: string, type: string): Observable<ImageMetadata> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    return this.http.get<ImageMetadata>(
      `${this.apiUrl}/organization/${orgId}/image/${type}`,
      httpOptions
    );
  }

  /**
   * Get a signed URL for retrieving an image from S3
   * @param filename The name of the file to retrieve
   * @returns Observable with the signed get URL
   */
  getImageUrl(filename: string): Observable<{ url: string }> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    const body = JSON.stringify({ Key: filename });
    return this.http.post<{ url: string }>(
      `${this.apiUrl}/docstore/getsignedgeturl`,
      body,
      httpOptions
    );
  }

  /**
   * Delete an image and its metadata
   * @param imageId The ID of the image to delete
   * @returns Observable with the deletion response
   */
  deleteImage(imageId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      withCredentials: true
    };
    return this.http.delete(`${this.apiUrl}/image/metadata/${imageId}`, httpOptions);
  }

  /**
   * Convert a base64 data URL to a File object
   * @param dataUrl The base64 data URL
   * @param filename The filename for the file
   * @returns A File object
   */
  base64ToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Validate if a file is an accepted image type
   * @param file The file to validate
   * @param acceptedTypes Array of accepted MIME types
   * @returns true if the file is valid, false otherwise
   */
  validateFileType(file: File, acceptedTypes: string[]): boolean {
    return acceptedTypes.includes(file.type);
  }

  /**
   * Validate if a file is within the maximum size limit
   * @param file The file to validate
   * @param maxSize Maximum file size in bytes
   * @returns true if the file is valid, false otherwise
   */
  validateFileSize(file: File, maxSize: number): boolean {
    return file.size <= maxSize;
  }
}
